import { z } from "zod";

import {
  DRAFT_CLASSES,
  DRAFT_KINDS,
  RECORD_TABLES,
  type DraftClass,
  type RecordTable,
} from "./v2-classes";
import { drawingVariableNameSchema, vec3Schema, type Vec3 } from "./v2-variables";

/**
 * Elements, records and operations of contracts v2 (RFC 0002 §1.1; `orthos_model`). Elements are our envelope
 * plus the engine's own record under `geometry`, kept as JSON; records are `{table, id, handle, …fields}`; operations
 * are per field with JSON values. Lengths are in drawing units (D-031).
 */

/** A JSON value: geometry, record fields and operation values carry them. */
export type JsonValue =
  string | number | boolean | null | readonly JsonValue[] | { readonly [key: string]: JsonValue };

export const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(z.string(), jsonValueSchema),
  ]),
);

/** IDs are UUIDs in lower-case hyphenated form, as Rust writes them; new ones are UUIDv7 (04 §3.1). */
export const idSchema = z.string().regex(/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/);
/** A DWG handle: upper-case hexadecimal without leading zeros (RFC 0002 §2). */
export const handleSchema = z.string().regex(/^(0|[1-9A-F][0-9A-F]{0,15})$/);
/** Where predicted objects' handles start; the server's operations replace them. */
export const PROVISIONAL_HANDLE_BASE = 0x4000_0000_0000_0000n;

/** The common properties, in the engine's shapes (`orthos_draft_doc::Common`). */
export type Color =
  | { readonly kind: "byLayer" }
  | { readonly kind: "byBlock" }
  | { readonly kind: "index"; readonly value: number }
  | { readonly kind: "true"; readonly value: readonly [number, number, number] };
export type Lineweight = "byLayer" | "byBlock" | "default" | { readonly mm100: number };
export type Transparency = "byLayer" | "byBlock" | { readonly percent: number };
/** `ByLayer`, `ByBlock` or a linetype record's ID. */
export type LinetypeRef = string;
export type Space = "model" | `sheet:${string}` | `block:${string}`;
export type Source = "user" | "ai" | "script" | "import" | "generator";
export type Via = "lisp" | "scr" | "macro" | "ts" | "app" | "cli";

export interface AnnoScale {
  readonly name: string;
  readonly paperUnits: number;
  readonly drawingUnits: number;
}
export interface RawTag {
  readonly code: number;
  readonly value: string;
}
export interface SourceDetail {
  readonly via?: Via | undefined;
  readonly id?: string | undefined;
  readonly name?: string | undefined;
  readonly version?: string | undefined;
}
export interface Meta {
  readonly createdBy: string;
  readonly createdAt: number;
  readonly modifiedBy: string;
  readonly modifiedAt: number;
  readonly source: Source;
  readonly sourceDetail?: SourceDetail | undefined;
  readonly approvedBy?: string | undefined;
}
/** The engine's record for the kind, references as IDs; `type` is the kind's tag. */
export interface Geometry {
  readonly type: string;
  readonly [key: string]: JsonValue;
}
export interface Element {
  readonly class: DraftClass;
  readonly id: string;
  readonly handle: string;
  readonly owner: { readonly drawing: string; readonly space: Space };
  readonly order: string;
  readonly layer: string;
  readonly color: Color;
  readonly linetype: LinetypeRef;
  readonly lineweight: Lineweight;
  readonly ltscale: number;
  readonly transparency: Transparency;
  readonly visible: boolean;
  readonly thickness: number;
  readonly extrusion: Vec3;
  readonly plotStyle: string;
  readonly annoScales?: readonly AnnoScale[] | undefined;
  readonly xdata?: readonly RawTag[] | undefined;
  readonly meta: Meta;
  readonly geometry: Geometry;
}

const colorSchema: z.ZodType<Color> = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("byLayer") }),
  z.strictObject({ kind: z.literal("byBlock") }),
  z.strictObject({ kind: z.literal("index"), value: z.number().int().min(0).max(255) }),
  z.strictObject({ kind: z.literal("true"), value: z.tuple([z.number(), z.number(), z.number()]) }),
]);
const byName = z.enum(["byLayer", "byBlock"]);
const spaceSchema = z.custom<Space>(
  (text) => typeof text === "string" && /^(model|(sheet|block):[0-9a-f-]{36})$/.test(text),
);

export const elementSchema: z.ZodType<Element> = z
  .strictObject({
    class: z.enum(DRAFT_CLASSES),
    id: idSchema,
    handle: handleSchema,
    owner: z.strictObject({ drawing: idSchema, space: spaceSchema }),
    order: z.string().regex(/^[0-9A-Za-z]+$/),
    layer: idSchema,
    color: colorSchema,
    linetype: z.union([z.enum(["ByLayer", "ByBlock"]), idSchema]),
    lineweight: z.union([
      byName,
      z.literal("default"),
      z.strictObject({ mm100: z.number().int() }),
    ]),
    ltscale: z.number(),
    transparency: z.union([byName, z.strictObject({ percent: z.number().int() })]),
    visible: z.boolean(),
    thickness: z.number(),
    extrusion: vec3Schema,
    plotStyle: z.string(),
    annoScales: z
      .array(z.strictObject({ name: z.string(), paperUnits: z.number(), drawingUnits: z.number() }))
      .optional(),
    xdata: z.array(z.strictObject({ code: z.number().int(), value: z.string() })).optional(),
    meta: z.strictObject({
      createdBy: z.string(),
      createdAt: z.number(),
      modifiedBy: z.string(),
      modifiedAt: z.number(),
      source: z.enum(["user", "ai", "script", "import", "generator"]),
      sourceDetail: z
        .strictObject({
          via: z.enum(["lisp", "scr", "macro", "ts", "app", "cli"]).optional(),
          id: z.string().optional(),
          name: z.string().optional(),
          version: z.string().optional(),
        })
        .optional(),
      approvedBy: z.string().optional(),
    }),
    geometry: z.object({ type: z.string().min(1) }).catchall(jsonValueSchema),
  })
  .refine((element) => element.geometry.type === DRAFT_KINDS[element.class], {
    message: "geometry.type is the tag of the element's class",
  });

/** A row of a drawing table: our envelope, then the engine's fields with references as IDs. */
export interface DrawingRecord {
  readonly table: RecordTable;
  readonly id: string;
  readonly handle: string;
  readonly [field: string]: JsonValue;
}

export const recordSchema: z.ZodType<DrawingRecord> = z
  .object({ table: z.enum(RECORD_TABLES), id: idSchema, handle: handleSchema })
  .catchall(jsonValueSchema);

/** Element fields an update may name: these, or `geometry.<key>` (not `type`). */
export const ENTITY_FIELDS = [
  "owner",
  "order",
  "layer",
  "color",
  "linetype",
  "lineweight",
  "ltscale",
  "transparency",
  "visible",
  "thickness",
  "extrusion",
  "plotStyle",
  "annoScales",
  "xdata",
] as const;
export type EntityField = (typeof ENTITY_FIELDS)[number] | `geometry.${string}`;

export function isEntityField(field: string): field is EntityField {
  const key = field.startsWith("geometry.") ? field.slice("geometry.".length) : undefined;
  if (key !== undefined) return key !== "" && key !== "type" && !key.includes(".");
  return (ENTITY_FIELDS as readonly string[]).includes(field);
}

/** One field from `before` to `after`; null stands for an absent field. */
export interface FieldChange<F extends string> {
  readonly field: F;
  readonly before: JsonValue;
  readonly after: JsonValue;
}

export type Operation =
  | { readonly type: "createEntity"; readonly entity: Element }
  | {
      readonly type: "updateEntity";
      readonly id: string;
      readonly changes: readonly FieldChange<EntityField>[];
    }
  | { readonly type: "deleteEntity"; readonly entity: Element }
  | { readonly type: "createRecord"; readonly record: DrawingRecord }
  | {
      readonly type: "updateRecord";
      readonly table: RecordTable;
      readonly id: string;
      readonly changes: readonly FieldChange<string>[];
    }
  | { readonly type: "deleteRecord"; readonly record: DrawingRecord }
  | {
      readonly type: "setVariable";
      readonly name: string;
      readonly before: JsonValue;
      readonly after: JsonValue;
    };

const entityChange = z.strictObject({
  field: z.custom<EntityField>((field) => typeof field === "string" && isEntityField(field)),
  before: jsonValueSchema,
  after: jsonValueSchema,
});
const recordChange = z.strictObject({
  field: z
    .string()
    .refine((f) => f !== "" && !f.includes(".") && !["table", "id", "handle"].includes(f)),
  before: jsonValueSchema,
  after: jsonValueSchema,
});

export const operationSchema: z.ZodType<Operation> = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("createEntity"), entity: elementSchema }),
  z.strictObject({ type: z.literal("updateEntity"), id: idSchema, changes: z.array(entityChange) }),
  z.strictObject({ type: z.literal("deleteEntity"), entity: elementSchema }),
  z.strictObject({ type: z.literal("createRecord"), record: recordSchema }),
  z.strictObject({
    type: z.literal("updateRecord"),
    table: z.enum(RECORD_TABLES),
    id: idSchema,
    changes: z.array(recordChange),
  }),
  z.strictObject({ type: z.literal("deleteRecord"), record: recordSchema }),
  z.strictObject({
    type: z.literal("setVariable"),
    name: drawingVariableNameSchema,
    before: jsonValueSchema,
    after: jsonValueSchema,
  }),
]);
